import React, { useState, useEffect, useRef } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../../api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faPhoneSlash,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import './HumanTaskPhoneDialer.scss';

interface HumanTaskPhoneDialerProps {
  taskId: string;
  phoneNumber: string; // Initial phone number from canvas props
  contactName?: string;
}

type CallStatus =
  | 'idle'
  | 'loading'
  | 'initializing'
  | 'connecting'
  | 'connected'
  | 'ended'
  | 'error';

const HumanTaskPhoneDialer: React.FC<HumanTaskPhoneDialerProps> = ({
  taskId,
  phoneNumber: initialPhoneNumber,
  contactName: initialContactName,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [callStatus, setCallStatus] = useState<CallStatus>('loading');
  const [callDuration, setCallDuration] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [callId, setCallId] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>(initialPhoneNumber);
  const [contactName, setContactName] = useState<string | undefined>(
    initialContactName,
  );
  const [transcriptionInput, setTranscriptionInput] = useState<string>('');
  const [isRequestingGuidance, setIsRequestingGuidance] =
    useState<boolean>(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [agentId, setAgentId] = useState<string | null>(null);
  const [isEditingPhone, setIsEditingPhone] = useState<boolean>(false);
  const [editedPhoneNumber, setEditedPhoneNumber] =
    useState<string>(initialPhoneNumber);

  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch latest task details on mount
  useEffect(() => {
    const fetchTaskDetails = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await api.get(`/human-tasks/${taskId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success && response.data.task) {
          const task = response.data.task;
          const details = task.taskDetails;

          // Store thread and agent IDs for system message
          setThreadId(task.threadId);
          setAgentId(task.createdByAgentId);

          // Update with latest data from database
          if (details.phoneNumber) {
            setPhoneNumber(details.phoneNumber);
          }
          if (details.contactName) {
            setContactName(details.contactName);
          }
        }

        setCallStatus('idle');
      } catch (error) {
        console.error('Failed to fetch task details:', error);
        // Fall back to prop values if fetch fails
        setCallStatus('idle');
      }
    };

    fetchTaskDetails();
  }, [taskId, getAccessTokenSilently]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      if (callRef.current) {
        callRef.current.disconnect();
      }
      if (deviceRef.current) {
        deviceRef.current.destroy();
      }
    };
  }, []);

  const formatPhoneNumber = (phone: string): string => {
    // Format phone number for display (e.g., +1 (555) 123-4567)
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startDurationTimer = () => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    setCallDuration(0);
    durationIntervalRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  };

  const stopDurationTimer = () => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  };

  const updateCallStatus = async (status: string, twilioCallSid?: string) => {
    if (!callId) return;

    try {
      const token = await getAccessTokenSilently();
      await api.post(
        `/calls/${callId}/status`,
        { status, twilioCallSid },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (error) {
      console.error('Failed to update call status:', error);
    }
  };

  const handleEditPhone = () => {
    setEditedPhoneNumber(phoneNumber);
    setIsEditingPhone(true);
  };

  const handleSavePhone = () => {
    // Normalize to E.164 format
    let normalized = editedPhoneNumber.trim();

    // Convert UK domestic (0131...) to international (+44131...)
    if (normalized.startsWith('0')) {
      normalized = `+44${normalized.substring(1)}`;
    }
    // Add + if missing
    else if (!normalized.startsWith('+')) {
      normalized = `+${normalized}`;
    }

    setPhoneNumber(normalized);
    setIsEditingPhone(false);
  };

  const handleCancelEditPhone = () => {
    setEditedPhoneNumber(phoneNumber);
    setIsEditingPhone(false);
  };

  const initiateCall = async () => {
    try {
      setCallStatus('initializing');
      setErrorMessage('');

      const token = await getAccessTokenSilently();

      // Step 1: Send system message to agent
      if (threadId && agentId) {
        try {
          await api.post(
            '/agents',
            {
              agent_id: agentId,
              thread_id: threadId,
              message: `SYSTEM_MESSAGE The ops team member is now calling ${phoneNumber}. 

🎧 LIVE PHONE COACHING MODE 🎧

You are now the VOICE of the ops team member. They will read your words EXACTLY as you write them to the person on the phone.

HOW IT WORKS:
- Messages prefixed with "📞 [Them]:" = what the person at ${phoneNumber} said. RESPOND to these with a SAY: line.
- Messages prefixed with "🎤 [You]:" = what our ops person has ALREADY said out loud. These are for your context only — DO NOT respond to them, just note they were said so you don't repeat yourself or contradict.
- Your response = the next line for our ops person to say (WILL BE READ VERBATIM)
- All transcripts arrive in English regardless of the language actually spoken on the call (Whisper translates on the fly). Reply in English unless the ops person explicitly switches to another language.

⚡ SPEED IS EVERYTHING ⚡

Phone conversations are FAST. The person is waiting. Every second of silence is awkward.

RULES:
1. START every response with "SAY:" - this is what they read out loud
2. ONE sentence max. Two if absolutely necessary.
3. NO thinking, NO explaining, NO context - just the line to say
4. Use contractions: "I'd like" not "I would like"
5. Short dates: "Friday 7pm" not "Friday the 15th of November at 7 o'clock"
6. No filler: Skip "um", "so", "well" unless natural

EXAMPLES OF GOOD RESPONSES:
- SAY: Hi, I'm calling to book a table for 4 on Friday at 7.
- SAY: Perfect, the name's Smith.
- SAY: Do you have anything at 8 instead?
- SAY: Great, thanks very much. Bye!

EXAMPLES OF BAD RESPONSES:
❌ "You should ask them about availability and then..." (NO - give the exact words!)
❌ "SAY: Hello there, I hope you're having a wonderful day, I was wondering if I might possibly enquire about..." (TOO LONG)
❌ "Let me think about how to respond to that..." (NO THINKING - just give the line!)

HANDLING THE CONVERSATION:
- They say something unexpected? Give the next line immediately.
- Need info? "SAY: What times do you have?"
- Confirming? "SAY: So that's Friday 7pm for 4 people under Smith, yeah?"
- Ending? "SAY: Lovely, thanks. Bye!"

The ops person is waiting. Be instant. Be brief. Just give them the words.

Reply "Ready" to confirm.`,
            },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } catch (error) {
          console.error('Failed to send system message:', error);
          // Continue anyway
        }
      }

      // Step 2: Show countdown (15 seconds)
      for (let i = 15; i >= 1; i--) {
        setCountdown(i);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      setCountdown(null);

      // Step 3: Initiate the actual call
      const response = await api.post(
        '/calls/initiate',
        { taskId, phoneNumber, contactName },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to initiate call');
      }

      const { callId: newCallId, token: twilioToken } = response.data;
      setCallId(newCallId);

      // Initialize Twilio Device
      const device = new Device(twilioToken, {
        logLevel: 1,
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      });

      deviceRef.current = device;

      // Register device event handlers
      device.on('registered', () => {
        console.log('Twilio Device registered');
      });

      device.on('error', (error: any) => {
        console.error('Twilio Device error:', error);
        setCallStatus('error');
        setErrorMessage('Device error: ' + error.message);
      });

      // Register the device
      await device.register();

      // Make the call
      setCallStatus('connecting');
      await updateCallStatus('connecting');

      // Pass `humanTaskCallId` through Twilio so the backend's /call-connect
      // webhook can pin the CallSid to the EXACT humanTaskCalls row without
      // having to guess by phone number. This makes the backend webhook a
      // reliable second-chance writer of twilioCallSid alongside the
      // updateCallStatus() POST below — whichever one wins the race wins
      // (the backend race-guards on twilioCallSid IS NULL). Without this,
      // if the frontend POST fails or is delayed, transcription setup in
      // routes/twilio-media-stream.ts (getCallContext) times out at 10s
      // and the live coaching flow breaks entirely.
      //
      // Use the LOCAL `newCallId`, not the React state `callId` — the
      // setCallId(newCallId) above hasn't propagated yet, so reading the
      // state here would send an empty string and the backend would refuse
      // to pin. Same closure trap bites updateCallStatus() throughout this
      // function; the backend webhook fix is what actually saves us when
      // those POSTs fire with stale `callId`.
      const call = await device.connect({
        params: {
          To: phoneNumber,
          humanTaskCallId: newCallId,
        },
      });

      callRef.current = call;

      // Register call event handlers
      call.on('accept', () => {
        console.log('Call accepted');
        setCallStatus('connected');
        startDurationTimer();
        updateCallStatus('connected', call.parameters.CallSid);
      });

      call.on('disconnect', () => {
        console.log('Call disconnected');
        setCallStatus('ended');
        stopDurationTimer();
        updateCallStatus('ended');
      });

      call.on('cancel', () => {
        console.log('Call cancelled');
        setCallStatus('ended');
        stopDurationTimer();
        updateCallStatus('ended');
      });

      call.on('reject', () => {
        console.log('Call rejected');
        setCallStatus('error');
        setErrorMessage('Call was rejected');
        updateCallStatus('ended');
      });

      call.on('error', (error: any) => {
        console.error('Call error:', error);
        setCallStatus('error');
        setErrorMessage('Call error: ' + error.message);
        stopDurationTimer();
        updateCallStatus('ended');
      });
    } catch (error: any) {
      console.error('Failed to initiate call:', error);
      setCallStatus('error');

      // Handle different error types
      let message = 'Failed to initiate call';
      if (error?.response?.data?.error) {
        message = error.response.data.error;
      } else if (error?.message) {
        message = error.message;
      } else if (typeof error === 'string') {
        message = error;
      }

      setErrorMessage(message);
    }
  };

  const hangUpCall = () => {
    if (callRef.current) {
      callRef.current.disconnect();
    }
    stopDurationTimer();
  };

  const sendDigit = (digit: string) => {
    if (callRef.current && callStatus === 'connected') {
      try {
        callRef.current.sendDigits(digit);
        console.log('Sent DTMF digit:', digit);
      } catch (error) {
        console.error('Failed to send digit:', error);
      }
    }
  };

  const requestAiGuidance = async () => {
    if (!transcriptionInput.trim() || !callId) return;

    setIsRequestingGuidance(true);

    try {
      const token = await getAccessTokenSilently();
      await api.post(
        `/calls/${callId}/request-guidance`,
        { transcription: transcriptionInput },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Clear input after sending
      setTranscriptionInput('');
      console.log('AI guidance requested - check the chat for response');
    } catch (error) {
      console.error('Failed to request guidance:', error);
      setErrorMessage('Failed to request AI guidance');
    } finally {
      setIsRequestingGuidance(false);
    }
  };

  const getStatusDisplay = (): { text: string; color: string } => {
    switch (callStatus) {
      case 'loading':
        return { text: 'Loading...', color: '#6c757d' };
      case 'idle':
        return { text: 'Ready to call', color: '#6c757d' };
      case 'initializing':
        return { text: 'Initializing...', color: '#0066cc' };
      case 'connecting':
        return { text: 'Connecting...', color: '#0066cc' };
      case 'connected':
        return { text: 'Connected', color: '#28a745' };
      case 'ended':
        return { text: 'Call ended', color: '#6c757d' };
      case 'error':
        return { text: 'Error', color: '#dc3545' };
      default:
        return { text: 'Unknown', color: '#6c757d' };
    }
  };

  const status = getStatusDisplay();
  const isCallActive =
    callStatus === 'connecting' || callStatus === 'connected';
  const isInitializing = callStatus === 'initializing';
  const isLoading = callStatus === 'loading';

  return (
    <div className='human-task-phone-dialer'>
      <div className='dialer-header'>
        <div className='contact-info'>
          <div className='phone-icon'>
            <FontAwesomeIcon icon={faPhone} />
          </div>
          <div className='contact-details'>
            {contactName && <div className='contact-name'>{contactName}</div>}
            {isEditingPhone ? (
              <div className='phone-number-edit'>
                <input
                  type='tel'
                  value={editedPhoneNumber}
                  onChange={(e) => setEditedPhoneNumber(e.target.value)}
                  className='phone-input'
                  placeholder='+44...'
                  autoFocus
                />
                <button
                  onClick={handleSavePhone}
                  className='btn-save-phone'
                  title='Save'
                >
                  ✓
                </button>
                <button
                  onClick={handleCancelEditPhone}
                  className='btn-cancel-phone'
                  title='Cancel'
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className='phone-number'>
                {formatPhoneNumber(phoneNumber)}
                {!isCallActive && !isInitializing && (
                  <button
                    onClick={handleEditPhone}
                    className='btn-edit-phone'
                    title='Edit phone number'
                  >
                    ✏️
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='dialer-status'>
        <div
          className='status-indicator'
          style={{ backgroundColor: status.color }}
        >
          {isInitializing && (
            <FontAwesomeIcon icon={faSpinner} spin className='status-icon' />
          )}
        </div>
        <div className='status-text' style={{ color: status.color }}>
          {status.text}
        </div>
      </div>

      {callStatus === 'connected' && (
        <div className='call-duration'>
          <div className='duration-label'>Duration</div>
          <div className='duration-time'>{formatDuration(callDuration)}</div>
        </div>
      )}

      {errorMessage && (
        <div className='error-message'>
          <strong>Error:</strong> {errorMessage}
        </div>
      )}

      <div className='dialer-controls'>
        {!isCallActive && !isInitializing && !isLoading && (
          <button
            className='btn-call'
            onClick={initiateCall}
            disabled={countdown !== null}
          >
            <FontAwesomeIcon icon={faPhone} />
            <span>{countdown !== null ? countdown : 'Call Now'}</span>
          </button>
        )}

        {isCallActive && (
          <button className='btn-hang-up' onClick={hangUpCall}>
            <FontAwesomeIcon icon={faPhoneSlash} />
            <span>Hang Up</span>
          </button>
        )}
      </div>

      {callStatus === 'connected' && (
        <div className='dial-pad'>
          <div className='dial-pad-header'>
            Press keys to navigate phone menu:
          </div>
          <div className='dial-pad-grid'>
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map(
              (digit) => (
                <button
                  key={digit}
                  className='dial-pad-button'
                  onClick={() => sendDigit(digit)}
                >
                  {digit}
                </button>
              ),
            )}
          </div>
        </div>
      )}

      <div className='dialer-instructions'>
        {callStatus === 'idle' && <p>Click "Call Now" to initiate the call.</p>}
        {callStatus === 'connected' && (
          <p>
            You are now connected. Use the dial pad above to navigate phone
            menus. Speak clearly and follow the task instructions.
          </p>
        )}
        {callStatus === 'ended' && (
          <p>
            The call has ended. Use the task completion button when you're ready
            to mark this task as complete.
          </p>
        )}
      </div>
    </div>
  );
};

export default HumanTaskPhoneDialer;
